import React, { useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import { IconChevronLeft, IconChevronRight, IconMinus, IconPlus } from '@tabler/icons-react'
import 'src/enviroment/utils/pdfWorker'
import Typography from '../../atoms/Typography'
import Loader from '../../atoms/Loader'

const MIN_SCALE = 0.5
const MAX_SCALE = 2
const SCALE_STEP = 0.1

interface DataRoomPdfPreviewProps {
  url: string
  onLoadError: () => void
}

const DataRoomPdfPreview: React.FC<DataRoomPdfPreviewProps> = ({ url, onLoadError }) => {
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1)
  const pageAreaRef = useRef<HTMLDivElement>(null)

  // a new page should be read from its top, not wherever the previous one was scrolled to
  useEffect(() => {
    pageAreaRef.current?.scrollTo({ top: 0 })
  }, [pageNumber])

  const goToPage = (delta: number) => setPageNumber((current) => Math.min(Math.max(current + delta, 1), numPages))
  const changeScale = (delta: number) =>
    setScale((current) => Math.min(Math.max(current + delta, MIN_SCALE), MAX_SCALE))

  return (
    <div className="flex h-full flex-col bg-gray-900">
      {/* grid rather than flex: the single cell fills the scroll area, so a loader or an error message
          centres in it while a taller page still scrolls from its top instead of being clipped */}
      <div ref={pageAreaRef} className="grid min-h-0 flex-1 place-items-center overflow-auto">
        <Document
          file={url}
          // a pageless pdf loads without error but has nothing to show, so treat it as a failure too
          onLoadSuccess={({ numPages: loadedPages }) => (loadedPages > 0 ? setNumPages(loadedPages) : onLoadError())}
          onLoadError={onLoadError}
          // an expired sas fails to fetch rather than to parse, and that arrives here instead of onLoadError
          onSourceError={onLoadError}
          className="flex flex-col items-center py-4"
          loading={<Loader className="text-light-green-500" />}
        >
          {/* pdf.js rejects a page request made before the document reports its count, so wait for it */}
          {numPages > 0 && (
            <Page
              pageNumber={pageNumber}
              scale={scale}
              className="shadow-lg"
              renderTextLayer={false}
              renderAnnotationLayer={false}
              // a page can fail after the document loaded fine, and that error never reaches Document's handler
              onLoadError={onLoadError}
              onRenderError={onLoadError}
              loading={<Loader className="text-light-green-500" />}
            />
          )}
        </Document>
      </div>

      {numPages > 0 && (
        <div className="flex items-center justify-center gap-6 border-t border-gray-500 px-4 py-2">
          <div className="flex items-center gap-2">
            <PreviewControl
              onClick={() => changeScale(-SCALE_STEP)}
              disabled={scale <= MIN_SCALE}
              label="Zoom out"
              icon={<IconMinus className="h-4 w-4" />}
            />
            <Typography variant="xs" className="w-12 text-center text-gray-200">
              {Math.round(scale * 100)}%
            </Typography>
            <PreviewControl
              onClick={() => changeScale(SCALE_STEP)}
              disabled={scale >= MAX_SCALE}
              label="Zoom in"
              icon={<IconPlus className="h-4 w-4" />}
            />
          </div>

          <div className="flex items-center gap-2">
            <PreviewControl
              onClick={() => goToPage(-1)}
              disabled={pageNumber <= 1}
              label="Previous page"
              icon={<IconChevronLeft className="h-4 w-4" />}
            />
            <Typography variant="xs" className="text-gray-200">
              Page {pageNumber} of {numPages}
            </Typography>
            <PreviewControl
              onClick={() => goToPage(1)}
              disabled={pageNumber >= numPages}
              label="Next page"
              icon={<IconChevronRight className="h-4 w-4" />}
            />
          </div>
        </div>
      )}
    </div>
  )
}

interface PreviewControlProps {
  onClick: () => void
  disabled: boolean
  label: string
  icon: React.ReactNode
}

const PreviewControl: React.FC<PreviewControlProps> = ({ onClick, disabled, label, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className="flex h-7 w-7 items-center justify-center rounded-full text-gray-50 transition-colors hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent"
  >
    {icon}
  </button>
)

export default DataRoomPdfPreview
